import { useRef, type ComponentPropsWithoutRef, type ElementType, type ReactNode, type Ref } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useMagnetic } from '../../lib/useMagnetic'

/**
 * Unified Helin button.
 *
 * Variants:
 *  - `primary`: gold background, ivory text, continuous gold-wave shimmer + sweep-shine
 *      and 1px lift on hover. The brand's load-bearing CTA.
 *  - `secondary`: outlined mocha → gold on hover. Used as the alternative when a
 *      primary is nearby (Pricing single tier, Membership unfeatured tiers).
 *  - `tertiary`: text-only link with hairline underline + arrow. The "or do this"
 *      link. Underline + text turn gold on hover, arrow nudges right.
 *
 * Sizes:
 *  - `sm` — navbar, inline contexts
 *  - `md` — section CTAs (FinalCTA, Pricing rows)
 *  - `lg` — hero / page-anchor CTAs
 *
 * Polymorphic via the `as` prop: render as <button> (default), <Link>, <a>, etc.
 * The `tertiary` variant auto-appends a trailing arrow icon — opt out with
 * `arrow={false}` if needed.
 */

type Variant = 'primary' | 'secondary' | 'tertiary'
type Size = 'sm' | 'md' | 'lg'
/** Tone of the surface the button sits on. Affects secondary/tertiary outline + text. */
type Tone = 'light' | 'dark'

interface CommonProps {
  variant?: Variant
  size?: Size
  /** Tone of the parent surface. `dark` flips secondary/tertiary to ivory. Default light. */
  tone?: Tone
  /** Show trailing arrow on tertiary variant. Default true. */
  arrow?: boolean
  /** Magnetic cursor pull. Opt-in, primary variant only. Auto-disabled on touch + reduced motion. */
  magnetic?: boolean
  className?: string
  children: ReactNode
}

type ButtonProps<T extends ElementType> = CommonProps & {
  as?: T
  /** Forwarded ref to the underlying element. */
  innerRef?: Ref<Element>
} & Omit<ComponentPropsWithoutRef<T>, keyof CommonProps | 'as'>

// --- Variant + size class maps ----------------------------------------------

// Primary: solid gold + animated shimmer + sweep on hover. Slight lift, gold glow.
// Tone-agnostic — gold reads on both light and dark surfaces.
const PRIMARY =
  'group relative overflow-hidden inline-flex items-center justify-center gap-2 whitespace-nowrap ' +
  'btn-gold-wave text-ivory rounded-lg ' +
  'shadow-gold-press hover:shadow-gold-press-strong hover:-translate-y-[1px] ' +
  'transition-all duration-300'

// Secondary: outlined, tone-aware. Light surface = mocha border/text. Dark = ivory.
const SECONDARY_BASE =
  'group relative inline-flex items-center justify-center gap-2 whitespace-nowrap ' +
  'border rounded-lg transition-colors duration-300 hover:border-gold hover:text-gold'
const SECONDARY_LIGHT = 'border-mocha/25 text-mocha'
const SECONDARY_DARK = 'border-ivory/40 text-ivory'

// Tertiary: text-only with hairline underline on the label. No padding/border/radius.
const TERTIARY_BASE = 'group inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-gold'
const TERTIARY_LIGHT = 'text-mocha/70'
const TERTIARY_DARK = 'text-ivory/70'

const SIZE_FILLED: Record<Size, string> = {
  sm: 'px-5 py-2 font-body text-xs tracking-[0.2em] uppercase font-medium',
  md: 'px-6 py-2.5 font-body text-xs tracking-[0.25em] uppercase font-medium',
  lg: 'px-7 py-3 font-body text-xs tracking-[0.25em] uppercase font-medium',
}

const SIZE_TERTIARY: Record<Size, string> = {
  sm: 'font-body text-xs',
  md: 'font-body text-sm',
  lg: 'font-body text-base',
}

function variantBase(variant: Variant, tone: Tone): string {
  switch (variant) {
    case 'primary':
      return PRIMARY
    case 'secondary':
      return cn(SECONDARY_BASE, tone === 'dark' ? SECONDARY_DARK : SECONDARY_LIGHT)
    case 'tertiary':
      return cn(TERTIARY_BASE, tone === 'dark' ? TERTIARY_DARK : TERTIARY_LIGHT)
  }
}

function classesFor(variant: Variant, size: Size, tone: Tone): string {
  const base = variantBase(variant, tone)
  const sizing = variant === 'tertiary' ? SIZE_TERTIARY[size] : SIZE_FILLED[size]
  return cn(base, sizing)
}

// --- Component --------------------------------------------------------------

function Button<T extends ElementType = 'button'>({
  as,
  variant = 'primary',
  size = 'md',
  tone = 'light',
  arrow = true,
  magnetic = false,
  className,
  children,
  innerRef,
  ...rest
}: ButtonProps<T>) {
  const Component = (as ?? 'button') as ElementType
  const classes = cn(classesFor(variant, size, tone), className)

  // Tertiary: render label inside a span so we can underline just the text,
  // not the arrow, and append a trailing arrow that nudges on hover.
  if (variant === 'tertiary') {
    const underlineClass = tone === 'dark' ? 'border-ivory/25' : 'border-mocha/20'
    return (
      <Component ref={innerRef} className={classes} {...rest}>
        <span className={`border-b ${underlineClass} group-hover:border-gold transition-colors pb-0.5`}>
          {children}
        </span>
        {arrow && (
          <ArrowRight
            size={14}
            aria-hidden="true"
            className="transition-transform group-hover:translate-x-0.5"
          />
        )}
      </Component>
    )
  }

  // Primary / secondary: filled rect with optional sweep-shine on hover (primary only).
  const inner = (
    <Component ref={innerRef} className={classes} {...rest}>
      {variant === 'primary' && (
        <span
          aria-hidden="true"
          className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 pointer-events-none"
        />
      )}
      <span className="relative inline-flex items-center gap-2 whitespace-nowrap">
        {children}
      </span>
    </Component>
  )

  // Magnetic effect — only on primary variant, opt-in. Wraps in a motion span
  // that translates toward the cursor. Auto-disables on touch + reduced motion.
  if (magnetic && variant === 'primary') {
    return <MagneticWrapper>{inner}</MagneticWrapper>
  }

  return inner
}

function MagneticWrapper({ children }: { children: ReactNode }) {
  const wrapRef = useRef<HTMLSpanElement | null>(null)
  const { x, y } = useMagnetic(wrapRef)
  return (
    <motion.span ref={wrapRef} style={{ x, y, display: 'inline-block' }}>
      {children}
    </motion.span>
  )
}

export default Button
